import {AssetsService} from '../Asset';
import {QuestionsService} from '../Questions';
import {
  PlacementTestService,
  InitWithAmplify,
  InitEnvironmentVariable,
} from '@PikaDoApp/npm-pikado-logic';
import {Amplify} from 'aws-amplify';
import {getCurrentUser} from 'aws-amplify/auth';
import {getEnvironment} from '../../lib/getEnvironment';

export class PlacementTestsService {
  assetsService: AssetsService;
  questionsService: QuestionsService;
  placementTestService: PlacementTestService;

  constructor() {
    this.assetsService = new AssetsService();
    this.questionsService = new QuestionsService();
    // Initialize Placement Test Service
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    this.placementTestService = new PlacementTestService();
  }

  // Create new placement test
  async createPlacementTest(placementTest: any): Promise<any> {
    try {
      // Get current user for createdBy field
      let createdBy = '';
      try {
        const user = await getCurrentUser();
        createdBy = user.signInDetails?.loginId || user.userId || 'unknown';
      } catch (error) {
        console.error('Error getting current user:', error);
        createdBy = 'unknown';
      }

      console.log('Creating questions first...');
      const questionIds = await Promise.all(
        placementTest.questions?.map(async (q: any) => {
          try {
            const questionId = await this.questionsService.createQuestion(q);
            return questionId;
          } catch (error) {
            console.error('Error creating question:', error);
            return null;
          }
        }) || []
      );

      // Filter out any failed question creations
      const validQuestionIds = questionIds.filter((id) => id !== null);
      console.log(`Created ${validQuestionIds.length} questions`);

      const media = placementTest.asset?.fileUri ? [{
        type: placementTest.asset.type?.toLowerCase() || 'video',
        url: placementTest.asset.fileUri,
      }] : [];

      const placementTestObj = {
        name: placementTest.name,
        description: placementTest.description,
        thumbnail: placementTest.asset?.thumbnailUri || placementTest.thumbnail || '',
        media: media, // Media array with asset data
        questions: validQuestionIds, // Question IDs array
        createdBy: createdBy,
        duration: placementTest.duration,
        startDate: placementTest.startDate,
        expirationDate: placementTest.expirationDate,
      };

      console.log('Creating placement test with pikado logic', {
        questionCount: validQuestionIds.length,
        hasMedia: media.length > 0,
        duration: placementTest.duration,
      });

      const createdPlacementTest = await this.placementTestService.newCreatePlacementTest(placementTestObj);

      // Transform response
      return {
        id: createdPlacementTest._id,
        _id: createdPlacementTest._id,
        name: createdPlacementTest.name,
        description: createdPlacementTest.description,
        thumbnail: createdPlacementTest.thumbnail,
        media: media,
        duration: createdPlacementTest.duration,
        startDate: createdPlacementTest.startDate,
        expirationDate: createdPlacementTest.expirationDate,
        questions: createdPlacementTest.questions,
        createdBy: createdPlacementTest.createdBy,
        createdAt: createdPlacementTest.createdAt,
        updatedAt: createdPlacementTest.updatedAt,
      };
    } catch (e) {
      console.error('Creating placement test error:', e);
      return new Promise((resolve, reject) =>
        reject(`Creating placement test error: ${e}`),
      );
    }
  }

  // Get all placement tests with pagination
  async getPlacementTests(page: number = 1, limit: number = 20): Promise<any> {
    try {
      const response = await this.placementTestService.findAllPlacementTests(page, limit);

      if (!response) {
        return {
          items: [],
          page: 1,
          totalPages: 0,
          total: 0,
        };
      }

      // Transform response - map _id to id for UI compatibility
      const items = response.data.map((test: any) => ({
        id: test._id,
        _id: test._id,
        oldId: test?.oldId,
        name: test.name,
        description: test.description,
        thumbnail: test.thumbnail,
        duration: test.duration,
        startDate: test.startDate,
        expirationDate: test.expirationDate,
        createdBy: test.createdBy,
        createdAt: test.createdAt,
        updatedAt: test.updatedAt,
      }));

      return {
        items,
        page: response.page,
        totalPages: response.totalPages,
        total: response.total,
      };
    } catch (error) {
      console.error('Error fetching placement tests:', error);
      return new Promise((resolve, reject) =>
        reject(`Error fetching placement tests: ${error}`),
      );
    }
  }

  // Search placement tests by name with pagination
  async searchPlacementTestsByName(
    name: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<any> {
    try {
      const response = await this.placementTestService.searchPlacementTestsByName(
        name,
        page,
        limit,
      );

      if (!response) {
        return {
          items: [],
          page: 1,
          totalPages: 0,
          total: 0,
        };
      }

      // Transform response - map _id to id (same pattern as getPlacementTests)
      const items = response.data.map((test: any) => ({
        id: test._id,
        _id: test._id,
        oldId: test?.oldId,
        name: test.name,
        description: test.description,
        thumbnail: test.thumbnail,
        duration: test.duration,
        startDate: test.startDate,
        expirationDate: test.expirationDate,
        createdBy: test.createdBy,
        createdAt: test.createdAt,
        updatedAt: test.updatedAt,
      }));

      return {
        items,
        page: response.page,
        totalPages: response.totalPages,
        total: response.total,
      };
    } catch (error) {
      console.error('Error searching placement tests:', error);
      return {
        items: [],
        page: 1,
        totalPages: 0,
        total: 0,
      };
    }
  }

  // Get placement test by ID
  async getPlacementTestById(id: string): Promise<any> {
    try {
      const test = await this.placementTestService.getPlacementTestById(id);

      if (!test) {
        throw new Error('Placement test not found');
      }

      // Transform media array to asset object for UI (matching TestsService pattern)
      let asset: any = undefined;
      if ((test as any).media && (test as any).media.length > 0) {
        const firstMedia = (test as any).media[0];

        // Map media types: 'video' -> 'VIDEO', 'image' -> 'PHOTO', etc.
        let assetType = 'VIDEO'; // default
        if (firstMedia.type) {
          const mediaTypeUpper = firstMedia.type.toUpperCase();
          if (mediaTypeUpper === 'IMAGE' || mediaTypeUpper === 'PHOTO') {
            assetType = 'PHOTO';
          } else if (mediaTypeUpper === 'VIDEO') {
            assetType = 'VIDEO';
          } else if (mediaTypeUpper === 'AUDIO') {
            assetType = 'AUDIO';
          } else if (mediaTypeUpper === 'PDF') {
            assetType = 'PDF';
          }
        }

        asset = {
          type: assetType,
          fileUri: firstMedia.url,
          thumbnailUri: test.thumbnail || firstMedia.url,
        };
      }

      // Transform response - map _id to id for UI compatibility
      return {
        id: test._id,
        _id: test._id,
        name: test.name,
        description: test.description,
        thumbnail: test.thumbnail,
        media: (test as any).media || [],
        asset: asset,
        duration: test.duration,
        startDate: test.startDate,
        expirationDate: test.expirationDate,
        // Map MongoDB question fields to UI format (same as TestsService)
        questions: test.questions?.map((q: any) => ({
          ...q,
          id: q._id || q.id,
          text: q.questionText || q.text || '',
          textAI: q.AiText || q.textAI || '',
          program: q.program?._id || q.program || '',
        })) || [],
        createdBy: test.createdBy,
        createdAt: test.createdAt,
        updatedAt: test.updatedAt,
      };
    } catch (error) {
      console.error('Error fetching placement test:', error);
      return new Promise((resolve, reject) =>
        reject(`Error fetching placement test: ${error}`),
      );
    }
  }

  // Update placement test
  async updatePlacementTest(placementTest: any, originalPlacementTest: any): Promise<any> {
    try {
      const newQuestionsIds: string[] = [];

      await Promise.all(
        placementTest.questions?.map(async (q: any) => {
          if (q.id || q._id) {
            // Existing question - update it
            return await this.questionsService.updateQuestion(
              q._id || q.id,
              q,
              q.updatedAt,
            );
          } else {
            // New question - create it
            const questionId = await this.questionsService.createQuestion(q);
            newQuestionsIds.push(questionId);
            return questionId;
          }
        }) || []
      );

      let questionIds: string[] = [];
      if (newQuestionsIds.length !== 0) {
        const existingQuestionIds = placementTest.questions
          .map((q: any) => q._id || q.id)
          .filter((id: string) => id);
        questionIds = existingQuestionIds.concat(newQuestionsIds);
      } else {
        questionIds = placementTest.questions
          ?.map((q: any) => q._id || q.id)
          .filter((id: string) => id) || [];
      }

      let media: Array<{ type: string; url: string }> | undefined;
      let thumbnail: string;

      if (placementTest.asset !== undefined) {
        // Asset was explicitly provided (user modified or removed it)
        // For PHOTO/IMAGE, the URL is in thumbnailUri; for VIDEO/AUDIO, it's in fileUri
        const assetUrl = placementTest.asset?.fileUri || placementTest.asset?.thumbnailUri;

        if (assetUrl) {
          // User set a new asset → construct new media and extract thumbnail
          media = [{
            type: placementTest.asset.type?.toLowerCase() || 'video',
            url: assetUrl,
          }];
          // Thumbnail from asset (priority: thumbnailUri > fileUri)
          thumbnail = placementTest.asset.thumbnailUri || placementTest.asset.fileUri || placementTest.thumbnail || '';
        } else {
          // User removed the asset → send empty media array and clear thumbnail
          media = [];
          thumbnail = '';
        }
      } else {
        // Asset was NOT provided (user didn't touch it) → preserve old media and thumbnail
        media = placementTest.media || originalPlacementTest.media || [];
        thumbnail = placementTest.thumbnail || originalPlacementTest.thumbnail || '';
      }

      const placementTestId = placementTest._id || placementTest.id;

      // Clean media array - remove __typename field added by GraphQL
      const cleanMedia = media?.map((item: any) => ({
        type: item.type,
        url: item.url,
      })) || [];

      const updateInput = {
        name: placementTest.name,
        description: placementTest.description,
        thumbnail: thumbnail,
        media: cleanMedia,
        duration: placementTest.duration,
        startDate: placementTest.startDate,
        expirationDate: placementTest.expirationDate,
      };

      // 🔍 DEBUG: Log the EXACT data being sent to backend
      console.log('==========================================');
      console.log('📤 PLACEMENT TEST UPDATE - DATA SENT TO BACKEND');
      console.log('==========================================');
      console.log('ID:', placementTestId);
      console.log('Asset in payload:', placementTest.asset !== undefined ? 'YES (modified)' : 'NO (preserved)');
      console.log('Media array:', media);
      console.log('Thumbnail:', thumbnail);
      console.log('INPUT:', JSON.stringify(updateInput, null, 2));
      console.log('==========================================');

      const updatedTest = await this.placementTestService.newUpdatePlacementTest(placementTestId, updateInput);

      if (questionIds.length > 0) {
        await this.placementTestService.addQuestionToPlacementTest(placementTestId, questionIds);
      }

      return {
        id: updatedTest._id,
        _id: updatedTest._id,
        name: updatedTest.name,
        description: updatedTest.description,
        thumbnail: updatedTest.thumbnail,
        media: cleanMedia, // Use the cleaned media we sent
        duration: updatedTest.duration,
        startDate: updatedTest.startDate,
        expirationDate: updatedTest.expirationDate,
        questions: questionIds, // Use tracked IDs, not from API response
        createdBy: updatedTest.createdBy,
        createdAt: updatedTest.createdAt,
        updatedAt: updatedTest.updatedAt,
      };
    } catch (e) {
      console.error('Updating placement test error:', e);
      return new Promise((resolve, reject) =>
        reject(`Updating placement test error: ${e}`),
      );
    }
  }

  // Delete placement test
  async deletePlacementTest(placementTestId: string): Promise<any> {
    try {
      console.log('Deleting placement test:', placementTestId);
      const result = await this.placementTestService.removePlacementTest(placementTestId);

      return {
        success: true,
        message: 'Placement test deleted successfully',
        deletedId: placementTestId,
        result,
      };
    } catch (error) {
      console.error('Error deleting placement test:', error);
      return new Promise((resolve, reject) =>
        reject(`Error deleting placement test: ${error}`),
      );
    }
  }

async updatePlacementTestQuestionsList(
  placementTestId: string,
  questionIds: string[]
): Promise<any> {
  try {

    await this.placementTestService.newUpdatePlacementTest(
      placementTestId,
      { questions: questionIds }
    );

    return {
      success: true,
      message: 'Questions list updated successfully'
    };

  } catch (error) {
    console.error('Error updating placement test questions list:', error);
    throw error;
  }
}
}